import { MapCacheAdapter } from '../flow-control/memo/adapter.mjs';
/**
 * A generic coordinator for cache and in-flight request deduplication.
 *
 * SingleFlight is a **state manager** that provides primitives for:
 * - Caching values with TTL and stale-while-revalidate (SWR)
 * - Tracking in-flight promises to prevent duplicate concurrent executions
 *
 * It does **NOT** handle execution - callers control the flow and use
 * SingleFlight as a coordination layer.
 *
 * **Core principles:**
 * - Generic - No knowledge of HTTP, routes, methods, etc. Just keys and values.
 * - Primitives, not executor - Provides building blocks, caller orchestrates.
 * - Reusable - Can be used in FetchEngine, database queries, memoize, anywhere.
 * - Composable - Consumers add their own routing, serialization, events.
 * - Async-first - All cache operations are async for adapter flexibility.
 *
 * @template T - The type of values to cache/track
 *
 * @example
 * ```typescript
 * // Basic usage - deduplication only
 * const flight = new SingleFlight();
 *
 * async function fetchUser(id: string) {
 *     const key = `user:${id}`;
 *
 *     // Check in-flight
 *     const inflight = flight.getInflight(key);
 *     if (inflight) {
 *         flight.joinInflight(key);
 *         return inflight.promise;
 *     }
 *
 *     // Start new request
 *     const promise = api.fetchUser(id);
 *     const cleanup = flight.trackInflight(key, promise);
 *
 *     try {
 *         return await promise;
 *     }
 *     finally {
 *         cleanup();
 *     }
 * }
 * ```
 *
 * @example
 * ```typescript
 * // With caching and SWR
 * const flight = new SingleFlight<UserData>({
 *     defaultTtl: 60000,     // 1 minute cache
 *     defaultStaleIn: 30000  // Stale after 30 seconds
 * });
 *
 * async function fetchUser(id: string) {
 *     const key = `user:${id}`;
 *
 *     // Check cache first
 *     const cached = await flight.getCache(key);
 *     if (cached && !cached.isStale) {
 *         return cached.value;
 *     }
 *
 *     // Check in-flight
 *     const inflight = flight.getInflight(key);
 *     if (inflight) {
 *         flight.joinInflight(key);
 *         return inflight.promise;
 *     }
 *
 *     // Return stale immediately, revalidate in background
 *     if (cached?.isStale) {
 *         const promise = api.fetchUser(id);
 *         const cleanup = flight.trackInflight(key, promise);
 *         promise
 *             .then(value => flight.setCache(key, value))
 *             .finally(cleanup);
 *         return cached.value;
 *     }
 *
 *     // Fresh fetch
 *     const promise = api.fetchUser(id);
 *     const cleanup = flight.trackInflight(key, promise);
 *     try {
 *         const value = await promise;
 *         await flight.setCache(key, value);
 *         return value;
 *     }
 *     finally {
 *         cleanup();
 *     }
 * }
 * ```
 */ export class SingleFlight {
    #adapter;
    #inflight = new Map();
    #defaultTtl;
    #defaultStaleIn;
    /**
     * Creates a new SingleFlight instance.
     *
     * @param opts - Configuration options
     */ constructor(opts){
        this.#defaultTtl = opts?.defaultTtl ?? 60000;
        this.#defaultStaleIn = opts?.defaultStaleIn;
        this.#adapter = opts?.adapter ?? new MapCacheAdapter({
            maxSize: opts?.maxSize ?? 1000,
            cleanupInterval: opts?.cleanupInterval ?? 60000
        });
    }
    // === Cache primitives ===
    /**
     * Get cached value if exists and not expired.
     *
     * Returns null if not cached or expired.
     * Returns `{ isStale: true }` if past staleAt but before expiresAt.
     *
     * @param key - Cache key
     * @returns Cache entry or null if not found/expired
     */ async getCache(key) {
        const item = await this.#adapter.get(key);
        if (!item) {
            return null;
        }
        const now = Date.now();
        if (now >= item.expiresAt) {
            await this.#adapter.delete(key);
            return null;
        }
        const isStale = item.staleAt !== undefined && now >= item.staleAt;
        return {
            value: item.value,
            isStale,
            expiresAt: item.expiresAt,
            staleAt: item.staleAt
        };
    }
    /**
     * Set a cache entry.
     *
     * @param key - Cache key
     * @param value - Value to cache
     * @param opts - Optional TTL and staleIn overrides
     */ async setCache(key, value, opts) {
        const ttl = opts?.ttl ?? this.#defaultTtl;
        const staleIn = opts?.staleIn ?? this.#defaultStaleIn;
        const now = Date.now();
        const item = {
            value,
            createdAt: now,
            expiresAt: now + ttl,
            staleAt: staleIn !== undefined ? now + staleIn : undefined
        };
        await this.#adapter.set(key, item, item.expiresAt);
    }
    /**
     * Delete a cache entry.
     *
     * @param key - Cache key
     * @returns true if entry existed
     */ async deleteCache(key) {
        return this.#adapter.delete(key);
    }
    /**
     * Check if key is cached (without returning value).
     *
     * Note: May return true for expired items if adapter doesn't clean eagerly.
     *
     * @param key - Cache key
     * @returns true if key exists in cache
     */ async hasCache(key) {
        const exists = await this.#adapter.has(key);
        if (!exists) {
            return false;
        }
        // Verify not expired
        const item = await this.#adapter.get(key);
        return item !== null && Date.now() < item.expiresAt;
    }
    // === In-flight primitives ===
    /**
     * Get in-flight entry if exists.
     *
     * @param key - Request key
     * @returns In-flight entry or null if not in-flight
     */ getInflight(key) {
        const entry = this.#inflight.get(key);
        if (!entry) {
            return null;
        }
        return {
            promise: entry.promise,
            waitingCount: entry.waitingCount
        };
    }
    /**
     * Track an in-flight promise.
     *
     * Returns cleanup function to call on completion.
     *
     * @param key - Request key
     * @param promise - The promise to track
     * @returns Cleanup function
     */ trackInflight(key, promise) {
        this.#inflight.set(key, {
            promise,
            waitingCount: 1
        });
        return ()=>{
            this.#inflight.delete(key);
        };
    }
    /**
     * Join an existing in-flight request.
     *
     * Increments waitingCount and returns new count.
     *
     * @param key - Request key
     * @returns New waiting count, or 0 if no in-flight request
     */ joinInflight(key) {
        const entry = this.#inflight.get(key);
        if (!entry) {
            return 0;
        }
        entry.waitingCount++;
        return entry.waitingCount;
    }
    /**
     * Check if key has in-flight request.
     *
     * @param key - Request key
     * @returns true if request is in-flight
     */ hasInflight(key) {
        return this.#inflight.has(key);
    }
    // === Lifecycle ===
    /**
     * Clear all state (cache + in-flight).
     */ async clear() {
        await this.clearCache();
        this.#inflight.clear();
    }
    /**
     * Clear only cache entries.
     */ async clearCache() {
        await this.#adapter.clear();
    }
    /**
     * Invalidate cache entries matching a predicate.
     *
     * Uses adapter's `keys()` method if available (MapCacheAdapter has it).
     * Returns 0 if adapter doesn't support key iteration.
     *
     * @param predicate - Function that returns true for keys to delete
     * @returns Number of entries deleted
     *
     * @example
     * ```typescript
     * // Delete all user-related entries
     * const count = await flight.invalidateCache(key => key.startsWith('user:'));
     * ```
     */ async invalidateCache(predicate) {
        const adapter = this.#adapter;
        if (typeof adapter.keys !== 'function') {
            return 0;
        }
        const keysToDelete = [];
        for (const key of adapter.keys()){
            if (predicate(key)) {
                keysToDelete.push(key);
            }
        }
        let deleted = 0;
        for (const key of keysToDelete){
            const wasDeleted = await this.#adapter.delete(key);
            if (wasDeleted) {
                deleted++;
            }
        }
        return deleted;
    }
    /**
     * Get statistics about current state.
     *
     * @returns Cache size and in-flight count
     */ stats() {
        return {
            cacheSize: this.#adapter.size,
            inflightCount: this.#inflight.size
        };
    }
}
